import { useContext, useEffect, useState } from "react"
import {
	Navigate,
	useNavigate,
	useParams,
	useSearchParams
} from "react-router-dom"
import { useTutorialAnalytics } from "~/hooks/useTutorialAnalytics"
import { toast } from "sonner"
import IssueDetailsPage from "./issue-details/page"
import KnowledgeBaseDocument from "./knowledge-base-document"
import { TutorialContext } from "~/components/tutorials/context"
import { OnboardingTour } from "~/components/tutorials/OnboardingTour"
import { OnboardingStep } from "~/components/tutorials/OnboardingStep"
import { Badge } from "~/components/ui/badge"
import { Button } from "~/components/ui/button"
import {
	useOnboardingData,
	onboardingDescription,
	onboardingArticle
} from "~/hooks/useOnboardingData"
import { IssueStatus } from "~/lib/enums"
import { getEditorPlainText } from "~/lib/utils"
import { tutorials } from "~/lib/tutorials"
import { api } from "~/trpc/react"

const tutorial = tutorials["onboarding-v1"]
const tasks = tutorial.steps
const guideStep = tasks.findIndex((task) => task.page === "guide") + 1

export default function OnboardingPage() {
	const { onboardingSession } = useContext(TutorialContext)
	const { orgSlug } = useParams()
	return onboardingSession ? (
		<ActiveOnboarding key={onboardingSession} />
	) : (
		<Navigate replace to={`/${orgSlug}/me/issues`} />
	)
}

function ActiveOnboarding() {
	const coordinator = useContext(TutorialContext)
	const { orgSlug } = useParams()
	const navigate = useNavigate()
	const [search, setSearch] = useSearchParams()
	const guide = search.get("page") === "guide"
	const [step, setStep] = useState(guide ? guideStep : 1)
	const [finished, setFinished] = useState(false)
	const practice = useOnboardingData()
	const analytics = useTutorialAnalytics("onboarding-v1", step - 1)
	const utils = api.useUtils()
	const finish = api.user.finishTutorial.useMutation({
		onSuccess: () => {
			void utils.user.getTutorialPreferences.invalidate()
		},
		onError: () =>
			toast.error("Couldn't save tutorial progress. Please try again.")
	})
	useEffect(() => {
		setStep((current) =>
			guide ? guideStep : current === guideStep ? guideStep - 1 : current
		)
	}, [guide])

	const task = tasks[step - 1]!
	const completedTasks = {
		status: practice.issue.status === IssueStatus.InProgress,
		description:
			getEditorPlainText(practice.issue.description) !==
			getEditorPlainText(onboardingDescription),
		reply: practice.issue.comments.some(
			(comment) => comment.repliesToId === "practice-comment"
		),
		reaction: practice.issue.comments
			.find((comment) => comment.id === "practice-comment")
			?.reactions.some((reaction) => reaction.type === "ThumbsUp"),
		article:
			getEditorPlainText(practice.document.content) !==
			getEditorPlainText(onboardingArticle)
	} satisfies Record<(typeof tasks)[number]["id"], boolean | undefined>
	const complete = completedTasks[task.id] ?? false
	const coach = (
		<OnboardingStep
			step={step}
			task={task}
			total={tasks.length}
			complete={complete}
			pending={finish.isPending}
			onBack={() => {
				setStep(step - 1)
				if (guide) setSearch({})
			}}
			onAdvance={async (skipped) => {
				if (finish.isPending) return
				analytics.finishStep(skipped)
				if (step === tasks.length) {
					try {
						await finish.mutateAsync({
							tutorialId: "onboarding-v1",
							outcome: "completed"
						})
						setFinished(true)
						analytics.end("completed", "finished")
					} catch {
						/* Keep the step available for retry. */
					}
				} else {
					setStep(step + 1)
					if (tasks[step]?.page === "guide") setSearch({ page: "guide" })
				}
			}}
		/>
	)
	const actions = (
		<>
			<Badge variant="secondary">Practice mode; nothing will be saved.</Badge>
			<Button
				variant="ghost"
				size="sm"
				disabled={finish.isPending}
				onClick={async () => {
					try {
						await finish.mutateAsync({
							tutorialId: "onboarding-v1",
							outcome: "dismissed"
						})
						analytics.end("dismissed", "exit_button")
						coordinator.endOnboarding()
						navigate(`/${orgSlug}/me/issues`)
					} catch {
						/* Keep the page available for retry. */
					}
				}}
			>
				Exit tour
			</Button>
		</>
	)
	if (finished)
		return (
			<OnboardingTour
				completed
				onClose={() => {
					coordinator.endOnboarding()
					navigate(`/${orgSlug}/me/issues`)
				}}
			/>
		)
	return guide ? (
		<KnowledgeBaseDocument
			practice={practice}
			tutorialPrompt={coach}
			tutorialActions={actions}
		/>
	) : (
		<IssueDetailsPage
			practice={practice}
			tutorialPrompt={coach}
			tutorialActions={actions}
		/>
	)
}
